import {
  vipOneMnetV2Bindings,
  vipOneMnetV2Template,
  type ClinicianWorkbenchPatientRecordFieldPath,
  type ClinicianWorkbenchPatientRecordPayer,
  type ClinicianWorkbenchPatientRecordV1,
} from "@amplecare/contracts";

import { getPatientRecordValueByPath } from "../record/record-core.js";
import {
  isRedFlagAnswer,
  resolveQuestionnaireReviewPromptText,
} from "../questionnaire-entry-core.js";

import {
  formatCaseTime,
  formatDateWithMonthName,
  formatHeightText,
  formatPhoneDisplay,
  formatSexText,
  formatWeightText,
  formatYesNo,
} from "../record/format.js";
import { isPatientRecordFieldFilled } from "../record/readiness.js";
import {
  computeAgeYears,
  computeBmi,
  computeClinicalSignificanceItems,
  computeDvtScore,
  computeFallRiskScore,
  dvtRiskFactorRubric,
  dvtScoringKeyText,
} from "../record/scoring.js";
import {
  checkbox,
  docBanner,
  documentTitleBlock,
  escapeHtml,
  fieldValue,
  provenanceFooter,
  renderChartPackHtml,
  sectionBar,
  signatureRow,
  yesNoBoxes,
} from "./primitives.js";

type RecordInput = ClinicianWorkbenchPatientRecordV1;

/** Questionnaire content the History Form renders from. The tenant-served
 *  definition is injected by callers when available; omitting it falls back
 *  to the bundled VIP One MNET v2 fixture pair. */
export interface ChartPackQuestionnaireContent {
  questions: typeof vipOneMnetV2Template.version.questions;
  chartBindings: typeof vipOneMnetV2Bindings;
}

const fixtureQuestionnaireContent: ChartPackQuestionnaireContent = {
  questions: vipOneMnetV2Template.version.questions,
  chartBindings: vipOneMnetV2Bindings,
};

function kv(label: string, value: string | null, blankWidth = 110): string {
  return `<div class="kv"><span class="kv-label">${escapeHtml(label)}</span><span>${fieldValue(value, blankWidth)}</span></div>`;
}

function contactLine(
  label: string,
  contact: { name: string | null; relationship: string | null; phone: string | null },
): string {
  return `<div class="contact-grid"><span class="kv-label">${escapeHtml(label)}</span>
    <span>Name: ${fieldValue(contact.name, 90)}</span>
    <span>Relationship: ${fieldValue(contact.relationship, 60)}</span>
    <span>Phone: ${fieldValue(formatPhoneDisplay(contact.phone), 80)}</span></div>`;
}

function payerBlock(title: string, payer: ClinicianWorkbenchPatientRecordPayer): string {
  return `
    <div>
      <div class="row"><span class="row-label">${escapeHtml(title)}</span></div>
      ${kv("Company", payer.companyName)}
      ${kv("Phone", formatPhoneDisplay(payer.companyPhone))}
      ${kv("Address", payer.companyAddress)}
      ${kv("Policy #", payer.policyNumber)}
      ${kv("Group #", payer.groupNumber)}
      ${kv("Policy Holder Name", payer.policyHolderName)}
      ${kv("Policy Holder Relationship", payer.policyHolderRelationship)}
      ${kv("Policy Holder Address", payer.policyHolderAddress)}
      ${kv("Policy Holder Home Phone", formatPhoneDisplay(payer.policyHolderPhone))}
      ${kv("Employer", payer.employerName)}
      ${kv("Employer Phone", formatPhoneDisplay(payer.employerPhone))}
      ${kv("Employer Address", payer.employerAddress)}
    </div>`;
}

// Shannon (2026-09-01): when every anesthesia screen is negative the chart
// says so in her words — "no history of problems with anesthesia; denies
// malignant hyperthermia" — on the Pre-Op Record and the H&P alike.
function anesthesiaHistoryStatement(record: RecordInput): string {
  const history = record.clinical.anesthesiaHistory;

  if (history.priorComplications === false) {
    return history.malignantHyperthermia === true
      ? "See anesthesia history"
      : "No history of problems with anesthesia; denies malignant hyperthermia";
  }

  if (history.priorComplications === true || history.malignantHyperthermia === true) {
    return "See anesthesia history";
  }

  return "";
}

function reviewOfSystemsLines(record: RecordInput): string {
  const systemLabels: Record<string, string> = {
    cardiovascular: "Cardiovascular",
    pulmonary: "Pulmonary",
    renal: "Renal",
    hepatic: "Hepatic",
    neurological: "Neurological",
    gastrointestinal: "Gastrointestinal",
    endocrine: "Endocrine/Metabolic",
    oncologic: "Oncologic",
    infectiousDiseases: "Infectious Diseases",
    hematological: "Hematological",
    musculoskeletal: "Musculoskeletal",
    psychiatric: "Psychiatric",
    obgyn: "OB/GYN",
    other: "Other",
  };

  // Umbrella-flow questionnaires (v10+) ask ONE screening question per
  // system and skip the detail block on a "no" — so "Denies" for a system
  // must come from the umbrella answer, not from the absence of detail
  // findings. An umbrella "yes" with no recorded findings means the details
  // are still pending (interrupted call), which must never print as a
  // denial. Legacy per-question interviews (no umbrella keys in the
  // answers) keep the original behavior.
  const umbrellaKeyBySystem: Record<string, string> = {
    cardiovascular: "cardiac_system_umbrella",
    pulmonary: "pulmonary_system_umbrella",
    renal: "renal_system_umbrella",
    hepatic: "hepatic_system_umbrella",
    neurological: "neurologic_system_umbrella",
    gastrointestinal: "gastrointestinal_system_umbrella",
    endocrine: "endocrine_system_umbrella",
    hematological: "hematologic_system_umbrella",
    psychiatric: "psychiatric_system_umbrella",
    musculoskeletal: "musculoskeletal_problems",
    // v11 (Shannon 2026-09-01): oncology reuses its entry question; infectious
    // disease got its own umbrella.
    oncologic: "cancer_history",
    infectiousDiseases: "infectious_system_umbrella",
  };
  const questionnaireAnswers = record.clinical.questionnaireAnswers ?? {};
  const umbrellaMode = Object.values(umbrellaKeyBySystem).some(
    (umbrellaKey) => questionnaireAnswers[umbrellaKey] !== undefined,
  );

  return Object.entries(record.clinical.reviewOfSystems)
    .map(([key, review]) => {
      const label = systemLabels[key] ?? key;
      const positives = review.findings
        .map((finding) => `+${finding.label}`)
        .join(", ");
      const detail = review.detail ? `${positives ? ", " : ""}${review.detail}` : "";
      // "Denies" is an affirmative clinical statement — the patient was asked
      // and said no. Printing it for a system we simply have no data on
      // fabricated a negative cardiac history for a patient whose heart attack
      // and pacemaker answers were sitting in the questionnaire. Without any
      // interview data at all, say so instead.
      const interviewed = Object.keys(questionnaireAnswers).length > 0;
      const umbrellaKey = umbrellaKeyBySystem[key];
      const umbrellaAnswer = umbrellaKey
        ? questionnaireAnswers[umbrellaKey]
        : undefined;
      const noFindingsText =
        key === "obgyn" && record.patient.sex === "male"
          ? "N/A"
          : umbrellaMode && umbrellaKey !== undefined
            ? umbrellaAnswer === false
              ? "Denies"
              : umbrellaAnswer === true
                ? "Positive on screening — details pending in questionnaire"
                : "Not obtained"
            : interviewed
              ? "Denies"
              : "Not obtained";
      const contentText =
        positives.length > 0 || detail.length > 0
          ? escapeHtml(`${positives}${detail}`)
          : noFindingsText;

      return `<div class="ros-row"><span class="kv-label">${escapeHtml(label)}:</span><span>${contentText}</span></div>`;
    })
    .join("\n");
}

function medicationsBlock(record: RecordInput): string {
  if (record.clinical.noKnownMedications === true) {
    return `<div class="prose-line value">No Medications</div>`;
  }

  if (record.clinical.medications.length === 0) {
    return `<div class="prose-line">${fieldValue(null, 220)}</div>`;
  }

  const rows = record.clinical.medications
    .map(
      (medication) =>
        `<tr><td>${escapeHtml(medication.name)}</td><td>${escapeHtml(medication.dose ?? "")}</td><td>${escapeHtml(medication.frequency ?? "")}</td><td>${escapeHtml(medication.reason ?? "")}</td></tr>`,
    )
    .join("");

  return `<table class="grid"><tr><th>Medication</th><th>Dose</th><th>Frequency</th><th>Reason</th></tr>${rows}</table>`;
}

function allergiesBlock(record: RecordInput): string {
  if (record.clinical.noKnownAllergies === true) {
    return `<div class="prose-line value" style="color:#b00">No known allergies</div>`;
  }

  if (record.clinical.allergies.length === 0) {
    return `<div class="prose-line">${fieldValue(null, 220)}</div>`;
  }

  const rows = record.clinical.allergies
    .map(
      (allergy) =>
        `<tr><td>${escapeHtml(allergy.category)}</td><td>${escapeHtml(allergy.substance)}</td><td>${escapeHtml(allergy.reaction ?? "")}</td></tr>`,
    )
    .join("");

  return `<table class="grid"><tr><th>Category</th><th>Allergy</th><th>Reaction</th></tr>${rows}</table>`;
}

function surgicalHistoryBlock(record: RecordInput): string {
  if (record.clinical.noSurgicalHistory === true) {
    return `<div class="prose-line value">No Surgical History</div>`;
  }

  if (record.clinical.surgicalHistory.length === 0) {
    return `<div class="prose-line">${fieldValue(null, 220)}</div>`;
  }

  const rows = record.clinical.surgicalHistory
    .map(
      (surgery) =>
        `<tr><td>${escapeHtml(surgery.surgery)}</td><td>${escapeHtml(surgery.approximateDate ?? "")}</td><td>${escapeHtml(surgery.problems ?? "")}</td></tr>`,
    )
    .join("");

  return `<table class="grid"><tr><th>Surgery</th><th>Date (approximate)</th><th>Problems</th></tr>${rows}</table>`;
}

// --- 1. Demographic & Insurance Information -------------------------------

function renderDemographicInsurance(record: RecordInput): string {
  const address = [
    record.patient.addressStreet,
    [record.patient.addressCity, record.patient.addressState]
      .filter((part) => part)
      .join(", "),
    record.patient.addressZip,
  ]
    .filter((part) => part && part.length > 0)
    .join(", ");

  return `
    ${documentTitleBlock({ record, title: "Demographic & Insurance Information" })}
    ${sectionBar("Personal information")}
    <div class="two-col">
      <div>
        ${kv(
          "Name",
          [record.patient.firstName, record.patient.lastName]
            .filter((part) => part)
            .join(" ") || null,
        )}
        ${kv("Date of Birth", formatDateWithMonthName(record.patient.dateOfBirth))}
        ${kv("Sex", formatSexText(record.patient.sex))}
        ${kv("Address", address || null)}
        ${kv("Email", record.patient.email)}
      </div>
      <div>
        <div class="kv"><span class="kv-label">Mobile Phone</span><span>${fieldValue(formatPhoneDisplay(record.patient.primaryPhone.number), 90)}
          ${checkbox(record.patient.primaryPhone.okayToLeaveVoicemail, "Okay to leave VM")}
          ${checkbox(record.patient.primaryPhone.weekdayNumber, "Weekday #")}</span></div>
        ${kv("Home Phone", formatPhoneDisplay(record.patient.homePhone.number))}
        ${kv("Work Phone", formatPhoneDisplay(record.patient.workPhone.number))}
        ${kv("Marital Status", record.patient.maritalStatus)}
        ${kv("Race", record.patient.race)}
        ${kv("Ethnicity", record.patient.ethnicity)}
        ${kv("Religious Affiliation", record.patient.religiousAffiliation)}
        ${kv("Primary Language", record.patient.primaryLanguage)}
        ${kv("Advance Directive", record.patient.advanceDirective)}
      </div>
    </div>
    ${sectionBar("Emergency contact")}
    ${contactLine("Emergency Contact", record.contacts.emergencyContact1)}
    ${contactLine("Other Emergency Contact", record.contacts.emergencyContact2)}
    <div class="two-col" style="margin-top:6px">
      <div>
        ${sectionBar("Primary health insurance")}
        ${payerBlock("Primary", record.insurance.primaryPayer)}
      </div>
      <div>
        ${sectionBar("Secondary health insurance")}
        ${
          record.insurance.secondaryPayerStatus === "none"
            ? `<div class="prose-line value">None</div>`
            : payerBlock("Secondary", record.insurance.secondaryPayer)
        }
      </div>
    </div>
    ${sectionBar("Care team")}
    <div class="contact-grid"><span class="kv-label">Primary Care Physician</span>
      <span>Name: ${fieldValue(record.contacts.primaryCarePhysician.name, 90)}</span>
      <span>Phone: ${fieldValue(formatPhoneDisplay(record.contacts.primaryCarePhysician.phone), 80)}</span>
      <span></span></div>
    ${[record.contacts.specialist1, record.contacts.specialist2, record.contacts.specialist3]
      .map(
        (specialist, index) =>
          `<div class="contact-grid"><span class="kv-label">Specialist ${String(index + 1)}</span>
            <span>Name: ${fieldValue(specialist.name, 90)}</span>
            <span>Specialty: ${fieldValue(specialist.specialty, 60)}</span>
            <span>Phone: ${fieldValue(formatPhoneDisplay(specialist.phone), 80)}</span></div>`,
      )
      .join("\n")}
    <div class="prose-line">${
      record.contacts.mayContactPhysicians === false
        ? `<span class="value">-- May NOT contact physician(s) --</span>`
        : record.contacts.mayContactPhysicians === true
          ? `<span class="value">May contact physician(s)</span>`
          : ""
    }</div>
    ${sectionBar("Ride home information")}
    ${contactLine("Ride Home", record.contacts.rideHome)}
    ${contactLine("Caregiver After Surgery", record.contacts.caregiver)}
    ${provenanceFooter(record)}`;
}

// --- 2. History & Physical --------------------------------------------------

function renderHistoryPhysical(record: RecordInput): string {
  const bmi = computeBmi(record.patient.heightInches, record.patient.weightPounds);

  return `
    ${documentTitleBlock({ record, title: "History & Physical" })}
    <div class="row">Ht: ${fieldValue(formatHeightText(record.patient.heightInches), 70)}
      Wt: ${fieldValue(formatWeightText(record.patient.weightPounds), 90)}
      BMI: ${fieldValue(bmi == null ? null : String(bmi), 40)}
      Smoking: ${fieldValue(formatYesNo(record.clinical.basicHistory.smoking.answer), 40)}
      ETOH: ${fieldValue(formatYesNo(record.clinical.basicHistory.alcohol.answer), 40)}
      Drug Hx: ${fieldValue(formatYesNo(record.clinical.basicHistory.streetDrugs.answer), 40)}</div>
    <div class="row">Preop Diagnosis: ${fieldValue(record.case.preopDiagnosis, 200)}</div>
    <div class="two-col" style="margin-top:6px">
      <div>
        ${sectionBar("Surgical/anesthesia history")}
        ${surgicalHistoryBlock(record)}
      </div>
      <div>
        ${sectionBar("Current medications")}
        ${medicationsBlock(record)}
        <div class="prose-line" style="margin-top:6px"><span class="row-label">Allergies/sensitivities:</span> See Pre-Operative Record (One True Source)</div>
      </div>
    </div>
    <div class="prose-line"><span class="row-label">HX OF ANESTHESIA COMPLICATIONS:</span> <span class="value" style="color:#b00">${
      anesthesiaHistoryStatement(record)
    }</span></div>
    ${sectionBar("Review of systems")}
    ${reviewOfSystemsLines(record)}
    ${sectionBar("Physical exam")}
    <div class="row">BP: ${fieldValue(null, 55)} / ${fieldValue(null, 55)} Pulse: ${fieldValue(null, 50)} Resp: ${fieldValue(null, 50)} O<sup>2</sup> Sat: ${fieldValue(null, 45)} % Temp: ${fieldValue(null, 50)} Pain: ${fieldValue(null, 40)}</div>
    <div class="row">${checkbox(null, "See attached H&P for Physical Exam")}</div>
    <div class="row"><span class="row-label">Mental Status:</span> ${checkbox(null, "A&O x 3")} ${checkbox(null, "Other:")} ${fieldValue(null, 160)}</div>
    <div class="row"><span class="row-label">Heart:</span> ${checkbox(null, "RRR and no murmurs")} ${fieldValue(null, 200)}</div>
    <div class="row"><span class="row-label">Lungs:</span> ${checkbox(null, "Clear to auscultation")} ${fieldValue(null, 200)}</div>
    <div class="row"><span class="row-label">Pertinent Physical Findings:</span> ${fieldValue(null, 240)}</div>
    ${sectionBar("Assessment")}
    <div class="prose-line">${checkbox(null, "Labs/Diagnostic tests reviewed")}</div>
    <div class="prose-line">${checkbox(null, "History and medical condition reviewed and patient is ready for surgery/procedure in an ambulatory setting.")}</div>
    <div class="prose-line">${checkbox(null, "Reviewed pertinent preoperative findings upon admission.")}</div>
    <div class="prose-line"><span class="row-label">Risks/Benefits and alternatives discussed with patient/family?</span> ${yesNoBoxes(null)} If no, explain: ${fieldValue(null, 150)}</div>
    ${signatureRow([{ label: "Physician Signature" }, { label: "Date" }])}
    ${provenanceFooter(record)}`;
}

// --- 3. Pre-Operative Record / One True Source ------------------------------

function renderPreopRecord(record: RecordInput): string {
  return `
    ${documentTitleBlock({ record, title: "Pre-Operative Record / One True Source" })}
    <div class="row">Stated Wt: ${fieldValue(formatWeightText(record.patient.weightPounds), 90)}
      Ht: ${fieldValue(formatHeightText(record.patient.heightInches), 70)}
      Actual Wt: ${fieldValue(null, 60)} Actual BMI: ${fieldValue(null, 50)}
      Time in Pre-Op: ${fieldValue(null, 70)}</div>
    <div class="row">Smoking: ${fieldValue(formatYesNo(record.clinical.basicHistory.smoking.answer), 40)}
      ETOH: ${fieldValue(formatYesNo(record.clinical.basicHistory.alcohol.answer), 40)}
      Drug Hx: ${fieldValue(formatYesNo(record.clinical.basicHistory.streetDrugs.answer), 40)}</div>
    <div class="two-col" style="margin-top:6px">
      <div>
        ${sectionBar("Surgical history")}
        ${surgicalHistoryBlock(record)}
      </div>
      <div>
        ${sectionBar("Current medications")}
        <div class="prose-line">Taken this A.M.? ${yesNoBoxes(null)}</div>
        ${medicationsBlock(record)}
        ${sectionBar("Allergies/sensitivities")}
        ${allergiesBlock(record)}
      </div>
    </div>
    <div class="prose-line"><span class="row-label">HX OF ANESTHESIA COMPLICATIONS:</span> <span class="value" style="color:#b00">${
      anesthesiaHistoryStatement(record)
    }</span></div>
    ${sectionBar("Review of systems")}
    ${reviewOfSystemsLines(record)}
    ${sectionBar("Notes / checklist")}
    <div class="two-col">
      <div>
        <div class="row">NPO: ${fieldValue(null, 90)}</div>
        <div class="row">DME: Cold / Sling / Brace / Shoe ${checkbox(null, "None")}</div>
        <div class="row">CONTACTS: ${yesNoBoxes(null)} IN / OUT</div>
        <div class="row">DENTURES: ${yesNoBoxes(null)} &#8593;&#8595; IN / OUT</div>
        <div class="row">JEWELRY: ${yesNoBoxes(null)}</div>
        <div class="row">GLASSES: ${yesNoBoxes(null)} ON / OFF</div>
        <div class="row">HEARING AID: ${yesNoBoxes(null)} L / R IN / OUT</div>
        <div class="row">PRE-OP, D/C Instructions Given ${yesNoBoxes(null)}</div>
        <div class="row">DVT ASSESSMENT REVIEWED ${checkbox(null, "")}</div>
      </div>
      <div>
        <div class="row"><span class="row-label">Vital Signs:</span></div>
        <div class="row">BP: ${fieldValue(null, 80)} P: ${fieldValue(null, 60)}</div>
        <div class="row">R: ${fieldValue(null, 60)} T: ${fieldValue(null, 60)}</div>
        <div class="row">O2Sat: ${fieldValue(null, 60)} Pain (1-10): ${fieldValue(null, 45)}</div>
        <div class="row"><span class="row-label">IV:</span> Time: ${fieldValue(null, 55)} Location ${fieldValue(null, 80)}</div>
        <div class="row">Cath Size ${fieldValue(null, 55)} Solution/Rate ${fieldValue(null, 80)}</div>
        <div class="row">By ${fieldValue(null, 90)} ${checkbox(null, "1% Lidocaine")} ${checkbox(null, "NA")}</div>
        <div class="row"># Attempts: ${fieldValue(null, 45)} Notes: ${fieldValue(null, 100)}</div>
      </div>
    </div>
    <div class="row" style="margin-top:10px">Time Ready for OR ${fieldValue(null, 80)} OR Nurse ${fieldValue(null, 110)}</div>
    ${signatureRow([{ label: "Pre-op Nurses" }, { label: "Date / Time" }])}
    ${provenanceFooter(record)}`;
}

// --- 4. Clinical Significance / Diagnostic Testing --------------------------

function renderClinicalSignificance(record: RecordInput): string {
  const items = computeClinicalSignificanceItems(record);

  return `
    ${documentTitleBlock({ record, title: "Clinical Significance/ Diagnostic Testing Information" })}
    <div class="two-col">
      <div>
        ${sectionBar("Allergies / sensitivities")}
        <div class="prose-line">See Pre-Operative Record (One True Source)</div>
      </div>
      <div>

      </div>
    </div>
    <div class="two-col" style="margin-top:10px">
      <div>
        <div class="row-label prose-line">CLINICALLY SIGNIFICANT ITEMS:</div>
        ${
          items.length > 0
            ? items
                .map((item) => `<div class="prose-line">${escapeHtml(item.label)}</div>`)
                .join("\n")
            : `<div class="prose-line">None identified from captured data.</div>`
        }
      </div>
      <div class="prose-line" style="text-align:center">${
        record.contacts.mayContactPhysicians === false
          ? `<span class="value">-- May NOT contact physician(s) --</span>`
          : ""
      }</div>
    </div>
    ${provenanceFooter(record)}`;
}

// --- 5. DVT Assessment -------------------------------------------------------

function renderDvtAssessment(record: RecordInput): string {
  const score = computeDvtScore(record);
  const points = (value: number | null) =>
    `<span class="score-points">${value == null ? "___" : String(value)} Pts.</span>`;
  const isFemale = record.patient.sex === "female";
  const womenOnlyRows = dvtRiskFactorRubric
    .filter((entry) => entry.womenOnly)
    .map((entry) => {
      const value = record.clinical.dvtRiskFactors[entry.key];
      const cellText = !isFemale
        ? "N/A Pts."
        : value == null
          ? "___ Pts."
          : `${value ? String(entry.points) : "0"} Pts.`;

      return `<div class="score-row"><span class="score-points">${cellText}</span><span>${escapeHtml(entry.label)}</span></div>`;
    })
    .join("\n");
  const generalRows = dvtRiskFactorRubric
    .filter((entry) => !entry.womenOnly)
    .map((entry) => {
      const value = record.clinical.dvtRiskFactors[entry.key];

      return `<div class="score-row"><span class="score-points">${
        value == null ? "___" : value ? String(entry.points) : "0"
      } Pts.</span><span>${escapeHtml(entry.label)}</span></div>`;
    })
    .join("\n");
  const lineFor = (key: string) => score.lines.find((line) => line.key === key);

  return `
    ${documentTitleBlock({ record, subtitle: "Page 1 of 2", title: "DVT Assessment" })}
    <p class="note">This protocol is intended to provide direction to the RN in the collection of clinical information to be provided to the patient's Physician for use in providing adequate VTE prophylaxis. Any patient total score of 6 or more, the RN will consult with the patient's Physicians.</p>
    ${sectionBar("Age")}
    <div class="score-row">${points(lineFor("age")?.points ?? null)}<span>${escapeHtml(lineFor("age")?.label ?? "Age")}</span></div>
    ${sectionBar("BMI")}
    <div class="score-row">${points(lineFor("bmi")?.points ?? null)}<span>${escapeHtml(lineFor("bmi")?.label ?? "BMI")}</span></div>
    ${sectionBar("Duration of surgery (including anesthesia time)")}
    <div class="score-row">${points(lineFor("duration")?.points ?? null)}<span>${escapeHtml(lineFor("duration")?.label ?? "Duration of surgery")}</span></div>
    ${sectionBar("Type of surgery")}
    <div class="score-row">${points(lineFor("surgery-type")?.points ?? null)}<span>${
      record.case.surgeryTypeCategory == null
        ? "Not captured"
        : escapeHtml(record.case.surgeryTypeCategory.replaceAll("-", " "))
    }</span></div>
    ${sectionBar("Women only")}
    ${womenOnlyRows}
    ${sectionBar("Intrinsic risk factors / miscellaneous")}
    ${generalRows}
    <div class="score-row" style="border-top:2px solid #111;font-size:12px">
      <span class="score-points">${score.totalPoints == null ? "___" : String(score.totalPoints)} Pts.</span>
      <span class="row-label">TOTAL SCORE (of a possible 20 points)${
        score.totalPoints == null && score.missingInputs.length > 0
          ? ` — unscored, missing: ${escapeHtml(score.missingInputs.join(", "))}`
          : ""
      }</span>
    </div>
    <div class="two-col" style="margin-top:8px">
      <div>
        <table class="grid">
          <tr><th>Total score</th><th>30-day VTE rate</th></tr>
          <tr><td>0-2</td><td>&lt; 0.1%</td></tr>
          <tr><td>3-5</td><td>0.1-0.3%</td></tr>
          <tr><td>6-10</td><td>0.3-0.5%</td></tr>
          <tr><td>11 or more</td><td>Up to 1.2%</td></tr>
        </table>
        ${
          score.vteRateText != null
            ? `<div class="prose-line value">This patient: ${escapeHtml(score.vteRateText)}</div>`
            : ""
        }
      </div>
      <div>
        <div class="row-label prose-line">Recommendations and Treatment Plan:</div>
        <div class="prose-line">X All patients receive teaching and early ambulation.</div>
        <div class="prose-line">${score.recommendScds ? "X" : "_"} Patients with a <b>Total Score of 6 or more</b> will have SCD's applied to non-operative leg(s), unless a reason is specified by the Physician.</div>
      </div>
    </div>
    ${signatureRow([{ label: "RN Signature" }, { label: "Time" }])}
    <p class="note">NOTE: The point total in this risk assessment includes intrinsic and surgical risk factors. However, despite the presence of intrinsic risk factors, if the surgery is not invasive enough to trigger the coagulation cascade, the risk of VTE is probably low.</p>
    <p class="note">NOTE: The reported occurrence rates are for patients who had treatment for VTE, and do not include patients who may have had an asymptomatic VTE.</p>
    ${renderDvtScoringKeyPage()}
    ${provenanceFooter(record)}`;
}

// Page 2 of the DVT Assessment: the point key nurses score against, printed
// verbatim from VIP's ONE MNET sheet ("DVT RISK ASSESSMENT SCORING").
function renderDvtScoringKeyPage(): string {
  const bandSection = (
    title: string,
    section: { note: string | null; bands: string[] },
  ) => `
    <div class="row-label prose-line" style="margin-top:10px">${escapeHtml(title)}</div>
    ${section.note ? `<div class="prose-line">${escapeHtml(section.note)}</div>` : ""}
    ${section.bands.map((band) => `<div class="prose-line">Points: ${escapeHtml(band)}</div>`).join("\n")}`;

  return `
    <div style="page-break-before:always">
      <div class="row-label prose-line" style="margin-top:12px">DVT RISK ASSESSMENT SCORING</div>
      ${bandSection("AGE FACTORS", dvtScoringKeyText.age)}
      ${bandSection("BMI FACTORS", dvtScoringKeyText.bmi)}
      ${bandSection("DURATION OF SURGERY FACTORS", dvtScoringKeyText.duration)}
      <div class="row-label prose-line" style="margin-top:10px">TYPE OF SURGERY FACTORS</div>
      ${dvtScoringKeyText.surgeryType.map((line) => `<div class="prose-line">${escapeHtml(line)}</div>`).join("\n")}
      <div class="row-label prose-line" style="margin-top:10px">INTRINSIC RISK FACTORS</div>
      ${dvtRiskFactorRubric
        .map(
          (entry) =>
            `<div class="prose-line">Points: ${entry.points}, ${escapeHtml(entry.label)}${entry.womenOnly ? " (Women Only)" : ""}</div>`,
        )
        .join("\n")}
    </div>`;
}

// --- 6. Fall Risk Assessment -------------------------------------------------

function renderFallRisk(record: RecordInput): string {
  const score = computeFallRiskScore(record);
  const rubricPoints: Record<string, number> = {
    age: 3,
    dizziness: 1,
    "impaired-mobility": 2,
    "visual-impairment": 2,
    confusion: 3,
    "altered-elimination": 3,
    "history-of-falls": 7,
    "home-meds": 2,
  };

  return `
    ${documentTitleBlock({ record, title: "Fall Risk Assessment" })}
    <div class="row" style="justify-content:flex-end"><span class="row-label">PRE</span>&nbsp;&nbsp;&nbsp;<span class="row-label">PRIOR TO DISCHARGE</span></div>
    <div class="prose-line note">PRE-OP ONLY — Yellow arm band placed on all patients scored 6+</div>
    ${score.lines
      .map(
        (line) => `
      <div class="score-row">
        <span style="flex:1"><span class="row-label">${escapeHtml(line.label.toUpperCase())}</span> (${String(rubricPoints[line.key] ?? "")} points)</span>
        <span class="score-points">${line.points == null ? "___" : String(line.points)}</span>
        <span class="score-points">___</span>
      </div>`,
      )
      .join("\n")}
    <div class="score-row" style="border-top:2px solid #111;font-size:12px">
      <span style="flex:1" class="row-label">TOTAL SCORE${
        score.totalPoints == null && score.missingInputs.length > 0
          ? ` — unscored, missing: ${escapeHtml(score.missingInputs.join(", "))}`
          : ""
      }</span>
      <span class="score-points">${score.totalPoints == null ? "___" : String(score.totalPoints)}</span>
      <span class="score-points">___</span>
    </div>
    <div class="prose-line" style="margin-top:8px">${checkbox(score.highRisk ? true : null, "High Risk Falls Intervention Implemented. *")} ${checkbox(null, "N/A")} <span class="note">*For a score of 6 points or more</span></div>
    <div class="prose-line" style="margin-top:8px"><span class="row-label">PEDIATRIC PATIENTS ONLY</span> — Falls Risk initiated for all pediatric patients — parents at bedside (circle correct response) YES / NO</div>
    ${signatureRow([{ label: "Pre-Op Nurse Signature" }, { label: "Time" }])}
    ${signatureRow([{ label: "Post-Op Nurse Signature" }, { label: "Time" }])}
    ${provenanceFooter(record)}`;
}

// --- 6b. Medication Reconciliation --------------------------------------------

const MAR_WRITE_IN_ROWS = 8;

// Mirrors the VIP Surgicare "Medication Reconciliation" form (chart pack page
// 13): Medication | Dose | Freq | Last Dose | Resume columns, the
// medications-given-at-surgery-center block, prescriptions, and the
// medication-instruction checkboxes. Last Dose and Resume stay blank for the
// pre-op RN; the interview fills only what the patient reported.
function renderMedicationReconciliation(record: RecordInput): string {
  const headerRow = `<tr><th>Medication</th><th>Dose</th><th>Freq</th><th>Last Dose</th><th>Resume</th></tr>`;
  const medicationRows =
    record.clinical.noKnownMedications === true
      ? `<tr><td colspan="5" class="value">No Medications</td></tr>`
      : record.clinical.medications
          .map(
            (medication) =>
              `<tr><td>${escapeHtml(medication.name)}</td><td>${escapeHtml(medication.dose ?? "")}</td><td>${escapeHtml(medication.frequency ?? "")}</td><td></td><td></td></tr>`,
          )
          .join("");
  // Blank rows print on purpose: patients forget medications, and nurses
  // hand-write additions on the printed sheet during the admit conversation.
  const writeInRows = Array.from(
    { length: MAR_WRITE_IN_ROWS },
    () => `<tr class="mar-write-in"><td>&nbsp;</td><td></td><td></td><td></td><td></td></tr>`,
  ).join("");
  const surgeryCenterRows = [
    [
      "EMEND (aprepitant) - nausea and vomiting",
      "Birth control medicines containing hormones to prevent pregnancy may be effected. Backup method of birth control (that do not contain hormones) such as condoms and spermicides should be used for at least 1 month.",
    ],
    [
      "BRIDION (Sugammadex) \u2013 reversal of neuromuscular blockade.",
      "Birth control medicines containing hormones to prevent pregnancy may be effected. Backup method of birth control (that do not contain hormones) such as condoms and spermicides should be used for 7 days",
    ],
    [
      "Tylenol (acetaminophen) - analgesic",
      "Do not take another dose of Tylenol until ____________. DO NOT take more than 3250 mg of Tylenol within a 24 hour period. Some pain medications and cold & cough medications contain Tylenol, please be alert.",
    ],
    [
      "Transderm-Scop (Scopolamine Patch)",
      "Scopolamine patch should be left in place for 24 hours after surgery. After 24 hours remove patch. Wash and dry hands thoroughly after removing patch. DO NOT drink alcohol or drive or operate heavy machinery while using scopolamine patch.",
    ],
  ]
    .map(
      ([medication, instructions]) =>
        `<tr><td style="width:220px">${checkbox(null, medication)}</td><td style="width:70px"></td><td style="font-size:8px">${escapeHtml(instructions)}</td></tr>`,
    )
    .join("");

  return `
    ${documentTitleBlock({ record, title: "Medication Reconciliation" })}
    <div class="prose-line" style="margin-top:6px"><span class="row-label">ALLERGIES/SENSITIVITIES:</span> ${checkbox(null, "See Allergy Documentation Form")}</div>
    ${sectionBar("Current Medication(s)")}
    <table class="grid">${headerRow}${medicationRows}${writeInRows}</table>
    ${signatureRow([
      { label: "Pre-Op RN Signature" },
      { label: "Date" },
      { label: "Time" },
    ])}
    ${sectionBar("Medication(s) Given at Surgery Center that may affect At-Home Care")}
    <table class="grid"><tr><th>Medication</th><th>Dose/Time</th><th>Special Instructions/Next Dose Due</th></tr>${surgeryCenterRows}</table>
    ${sectionBar("Prescriptions")}
    <table class="grid">
      <tr><th>Medication</th><th style="width:90px">Dose</th><th>Special Instructions</th></tr>
      <tr><td>&nbsp;</td><td></td><td></td></tr>
      <tr><td>&nbsp;</td><td></td><td></td></tr>
    </table>
    <div class="prose-line" style="margin-top:4px">${checkbox(null, "No Prescriptions")} OR Prescription given: ${checkbox(null, "In office")} ${checkbox(null, "To patient/family at discharge")} *copy placed on chart</div>
    <div class="row-label prose-line" style="margin-top:6px">Medication Instructions:</div>
    <div class="prose-line">${checkbox(null, "Stop the following medication(s)")} ______________________________________________ and resume in ________ days</div>
    <div class="prose-line">${checkbox(null, "Have patient continue previous medications except:")} ____________________________________________________</div>
    <div class="prose-line">${checkbox(null, "Do NOT take Aspirin, Advil, Motrin, Aleve, or any NSAID for")} ${checkbox(null, "1 week,")} ${checkbox(null, "2 weeks,")} ${checkbox(null, "________ days")}</div>
    <div class="prose-line">${checkbox(null, "If your physician instructed you to stop taking any medication prior to your procedure today, call the ordering physician for further instructions.")}</div>
    <div class="prose-line">${checkbox(null, "Patient/Family member was educated, and a copy was given to the patient and/or family and understanding of instructions was verbalized.")}</div>
    ${signatureRow([
      { label: "RN's SIGNATURE (patient education)" },
      { label: "Date" },
      { label: "PHYSICIAN SIGNATURE" },
      { label: "Date" },
    ])}
    ${signatureRow([
      { label: "Patient or Responsible Adult" },
      { label: "Relationship to Patient" },
      { label: "Date" },
    ])}
    ${provenanceFooter(record)}`;
}

// --- 7. Anesthesia Physical Exam ----------------------------------------------

function renderAnesthesiaExam(record: RecordInput): string {
  return `
    ${documentTitleBlock({ record, title: "Anesthesia Physical Exam" })}
    <table class="grid" style="margin-top:6px">
      <tr><td class="row-label" style="width:220px">Airway</td><td>Uvula and Tonsillar Pillars: ${checkbox(null, "Visible")} ${checkbox(null, "Partially Visible")} ${checkbox(null, "Not Visible")}</td></tr>
      <tr><td class="row-label">Mandibular ROM</td><td>${checkbox(null, "Good")} ${checkbox(null, "Other")} ${fieldValue(null, 180)}</td></tr>
      <tr><td class="row-label">Dentition</td><td>${checkbox(null, "Good")} ${checkbox(null, "Other")} ${fieldValue(null, 180)}</td></tr>
      <tr><td class="row-label">Neck</td><td>${checkbox(null, "Supple")} ${checkbox(null, "Other")} ${fieldValue(null, 180)}</td></tr>
      <tr><td class="row-label">Heart</td><td>${checkbox(null, "RRR w/o murmurs or rubs")} ${checkbox(null, "Other")} ${fieldValue(null, 150)}</td></tr>
      <tr><td class="row-label">Lungs</td><td>${checkbox(null, "Clear to auscultation")} ${checkbox(null, "Other")} ${fieldValue(null, 150)}</td></tr>
      <tr><td class="row-label">Other Pertinent Physical Findings</td><td>${checkbox(null, "None")} ${checkbox(null, "Other")} ${fieldValue(null, 170)}</td></tr>
      <tr><td class="row-label">Significant Abnormal Laboratory Or Diagnostic Studies</td><td>${checkbox(null, "None")} ${checkbox(null, "Other")} ${fieldValue(null, 170)}</td></tr>
      <tr><td class="row-label">ASA Physical Status</td><td>${checkbox(null, "1")} ${checkbox(null, "2")} ${checkbox(null, "3")} ${checkbox(null, "4")} ${checkbox(null, "E")}</td></tr>
      <tr><td class="row-label">Anesthesia Plan</td><td>${checkbox(null, "Conscious Sedation")} ${checkbox(null, "MAC")} ${checkbox(null, "GEN")} ${checkbox(null, "Local")} ${checkbox(null, "Regional")}</td></tr>
    </table>
    <div class="prose-line" style="margin-top:6px">${checkbox(null, "For Proceduralist only: See History & Physical for Physical Exam")}</div>
    <div class="prose-line">${checkbox(null, "Other")} ${fieldValue(null, 300)}</div>
    ${sectionBar("Additional comments or history not addressed")}
    <div style="border:1px solid #666;height:120px"></div>
    ${signatureRow([{ label: "Provider Signature" }, { label: "Date" }, { label: "Time" }])}
    ${provenanceFooter(record)}`;
}

// --- 8. Consent for Anesthesia -------------------------------------------------

function renderAnesthesiaConsent(record: RecordInput): string {
  return `
    ${documentTitleBlock({ record, title: "Consent for Anesthesia" })}
    <div class="consent-text" style="margin-top:10px">
      <p>I HAVE BEEN INFORMED OF THE BENEFITS AND RISKS OF ANESTHESIA. I UNDERSTAND THAT DURING THE COURSE OF MY PROCEDURE, I MAY NEED ADDITIONAL OR DIFFERENT ANESTHETIC MONITORING OR TECHNIQUES. I CONSENT TO THE USE OF SUCH ANESTHETICS AS MAY BE CONSIDERED NECESSARY.</p>
      <p>I authorize and direct anesthesia associates to perform anesthesia upon me and/or to do any other procedure that in his/her judgement may be advisable for my wellbeing. These may include General, Local, Regional or Monitored Anesthesia Care. Anesthesia involves the use of drugs and procedures. The I.V. medications given also have amnesic properties, that is, they may make you forget surgery. Not only do different individuals react differently to the same medications, but also the state of their physical health influences the action of medications. While all reasonable precautions will be taken, unforeseen reactions or complications may occur.</p>
      <p>I understand and accept the fact that certain hazards and risks are inherent in any anesthetic procedure. Post anesthetic nausea and vomiting, headache, phlebitis, or sore throat with laryngeal and tracheal swelling may occur. Even though steps are taken to protect bridgework, capped or unsound teeth, there is no guarantee against possible accidental damage, even to normal teeth.</p>
      <p>More serious heart and lung abnormalities or nerve and/or muscle disorders also may occur. No anesthetic procedure, general, local, spinal, epidural, or other, is any guarantee against the development on any complication. Although rare, there is always a remote risk of death, paralysis, or brain damage associated with the administration of anesthetics and no warranty or guarantees have been made as to the outcome.</p>
    </div>
    ${signatureRow([
      { label: "Patient Signature (or Patient's Guardian)" },
      { label: "Time" },
      { label: "Date" },
    ])}
    <div class="consent-text" style="margin-top:18px">
      <p>I HAVE EXAMINED THE PATIENT IMMEDIATELY PRE-OPERATIVELY AND HAVE DETERMINED THAT THIS PATIENT IS IN SUITABLE MEDICAL CONDITION TO UNDERGO THE PLANNED PROCEDURE, ANESTHESIA AND SEDATION. I HAVE EXPLAINED THE ANESTHETIC PLAN, INCLUDING APPROPRIATE ALTERNATIVES, SIDE EFFECTS AND RISKS. I HAVE ANSWERED ALL OF THE PATIENT'S (OR PATIENT'S GUARDIAN'S) QUESTIONS. THE PATIENT (OR PATIENT'S GUARDIAN) ACCEPTS THE PROPOSED PLAN.</p>
    </div>
    ${signatureRow([{ label: "Physician's Signature" }, { label: "Date" }])}
    ${provenanceFooter(record)}`;
}

// --- 9. History form (capture form) --------------------------------------------

function formatHistoryAnswerValue(value: unknown): string | null {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  if (typeof value === "boolean") {
    return formatYesNo(value);
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return null;
    return value
      .map((item) =>
        item != null && typeof item === "object"
          ? String(
              (item as { label?: unknown; value?: unknown }).label ??
                (item as { value?: unknown }).value ??
                "",
            )
          : String(item),
      )
      .filter((text) => text.length > 0)
      .join(", ");
  }
  return String(value);
}

// Demographic-predicate check for print: questions the template hides for this
// patient's sex/age print an explicit N/A rather than a blank that reads as
// "unanswered" (review finding 7 — the old form's obgyn N/A behavior).
function demographicNotApplicable(
  question: (typeof vipOneMnetV2Template.version.questions)[number],
  record: RecordInput,
): boolean {
  const onlyIf = question.skipLogic?.onlyIf;
  if (!onlyIf) return false;
  const sex = record.patient.sex;
  if (onlyIf.patientSexAtBirthOneOf && sex != null && !onlyIf.patientSexAtBirthOneOf.includes(sex)) {
    return true;
  }
  const age = computeAgeYears(record.patient.dateOfBirth, record.case.dateOfService);
  if (onlyIf.patientAgeYearsAtLeast !== undefined && age != null && age < onlyIf.patientAgeYearsAtLeast) {
    return true;
  }
  if (onlyIf.patientAgeYearsAtMost !== undefined && age != null && age > onlyIf.patientAgeYearsAtMost) {
    return true;
  }
  return false;
}

// The guaranteed catch-all (spec: no-orphan-answers rule): every question with
// a printable destination renders here from the v2 template + bindings, so the
// printed pack always carries the complete questionnaire even before a curated
// form field exists for an answer. Rows are tagged with data-question-key so
// the chart-pack coverage gate can verify completeness mechanically.
function renderHistoryForm(
  record: RecordInput,
  questionnaireContent?: ChartPackQuestionnaireContent | null,
): string {
  const { chartBindings, questions } =
    questionnaireContent ?? fixtureQuestionnaireContent;
  const bindingByKey = new Map(
    chartBindings.map((binding) => [binding.questionKey, binding]),
  );

  const sections: { label: string; rows: string[] }[] = [];
  const sectionIndex = new Map<string, number>();

  for (const question of questions) {
    const binding = bindingByKey.get(question.questionKey);
    // chartPackDestination is optional at the type level only (legacy overlay
    // tables); a binding without one declares no printable destination, which
    // the catch-all treats the same as operational-only.
    if (
      binding?.chartPackDestination == null ||
      binding.chartPackDestination.kind === "operational_only"
    ) {
      continue;
    }

    const notApplicable = demographicNotApplicable(question, record);
    const rawValue = notApplicable
      ? null
      : getPatientRecordValueByPath(record, binding.fieldPath);
    const value = notApplicable ? "N/A" : formatHistoryAnswerValue(rawValue);
    // "N/A" is the absence of an answer, not a positive finding — flagging it
    // printed a red "Are you pregnant? N/A" on every male patient's form.
    const flagged = !notApplicable && isRedFlagAnswer(question.redFlagRules, rawValue);
    const answerHtml =
      value == null
        ? fieldValue(null, 120)
        : `<span class="value"${flagged ? ' style="color:#b00"' : ""}>${escapeHtml(value)}</span>`;
    const reviewPromptText = resolveQuestionnaireReviewPromptText(
      question.questionKey,
      question.promptText ?? question.questionKey,
    );
    const row = `<div class="qa-row" data-question-key="${escapeHtml(question.questionKey)}"><span style="flex:1${flagged ? ";color:#b00" : ""}">${escapeHtml(reviewPromptText)}</span><span class="qa-answer">${answerHtml}</span></div>`;

    const sectionLabel = question.sectionLabel ?? question.sectionKey;
    let index = sectionIndex.get(sectionLabel);
    if (index === undefined) {
      index = sections.length;
      sectionIndex.set(sectionLabel, index);
      sections.push({ label: sectionLabel, rows: [] });
    }
    sections[index].rows.push(row);
  }

  return `
    ${documentTitleBlock({ record, title: "Questionnaire Worksheet (internal)" })}
    ${sections
      .map(
        (section) => `${sectionBar(section.label)}
    ${section.rows.join("\n")}`,
      )
      .join("\n")}
    ${sectionBar("Current medications")}
    ${medicationsBlock(record)}
    ${sectionBar("Allergies / sensitivities")}
    ${allergiesBlock(record)}
    ${sectionBar("Past surgical history")}
    ${surgicalHistoryBlock(record)}
    ${sectionBar("Demographic information")}
    ${contactLine("Emergency Contact", record.contacts.emergencyContact1)}
    <div class="row">Legal Guardian: ${yesNoBoxes(record.patient.legalGuardian)}</div>
    <div class="row">Primary Language: ${fieldValue(record.patient.primaryLanguage, 80)} Needs Translator: ${fieldValue(formatYesNo(record.patient.needsTranslator), 45)}</div>
    <div class="row">Address: ${fieldValue(record.patient.addressStreet, 130)} City/State/Zip: ${fieldValue(
      [record.patient.addressCity, record.patient.addressState, record.patient.addressZip]
        .filter((part) => part)
        .join(", ") || null,
      130,
    )}</div>
    <div class="row">Home Phone: ${fieldValue(formatPhoneDisplay(record.patient.homePhone.number), 90)} Mobile: ${fieldValue(formatPhoneDisplay(record.patient.primaryPhone.number), 90)} Work: ${fieldValue(formatPhoneDisplay(record.patient.workPhone.number), 90)}</div>
    ${[record.contacts.specialist1, record.contacts.specialist2, record.contacts.specialist3]
      .map(
        (specialist, index) =>
          `<div class="row">Specialist ${index + 1}: ${fieldValue(specialist.name, 100)} Specialty: ${fieldValue(specialist.specialty, 80)} Phone: ${fieldValue(formatPhoneDisplay(specialist.phone), 80)}</div>`,
      )
      .join("\n")}
    <div class="row">Name of Person Completing Form: ${fieldValue(null, 160)}</div>
    <div class="row" style="margin-top:6px">Additional Comments or History Not Addressed: ${fieldValue(null, 260)}</div>
    ${signatureRow([
      { label: "Patient Signature" },
      { label: "Date" },
      { label: "Nurse Signature" },
      { label: "Date" },
    ])}
    ${provenanceFooter(record)}`;
}

// --- Registry -----------------------------------------------------------------

export interface ChartPackDocumentDefinition {
  id: string;
  title: string;
  render: (
    record: ClinicianWorkbenchPatientRecordV1,
    questionnaireContent?: ChartPackQuestionnaireContent | null,
  ) => string;
  requiredFieldPaths: ClinicianWorkbenchPatientRecordFieldPath[];
}

const headerPaths: ClinicianWorkbenchPatientRecordFieldPath[] = [
  "patient.firstName",
  "patient.lastName",
  "patient.mrn",
  "patient.dateOfBirth",
  "patient.sex",
  "case.dateOfService",
  "case.surgeonName",
  "case.procedures",
];

export const chartPackDocuments: ChartPackDocumentDefinition[] = [
  {
    id: "demographic-insurance",
    title: "Demographic & Insurance Information",
    render: renderDemographicInsurance,
    requiredFieldPaths: [
      ...headerPaths,
      "patient.addressStreet",
      "patient.primaryPhone.number",
      "patient.email",
      "insurance.primaryPayer.companyName",
      "insurance.primaryPayer.policyNumber",
      "insurance.primaryPayer.groupNumber",
      "insurance.secondaryPayerStatus",
      "contacts.emergencyContact1.name",
      "contacts.rideHome.name",
      "contacts.rideHome.phone",
    ],
  },
  {
    id: "history-physical",
    title: "History & Physical",
    render: renderHistoryPhysical,
    requiredFieldPaths: [
      ...headerPaths,
      "patient.heightInches",
      "patient.weightPounds",
      "clinical.noKnownMedications",
      "clinical.noKnownAllergies",
      "clinical.noSurgicalHistory",
    ],
  },
  {
    id: "preop-record",
    title: "Pre-Operative Record / One True Source",
    render: renderPreopRecord,
    requiredFieldPaths: [
      ...headerPaths,
      "patient.heightInches",
      "patient.weightPounds",
      "clinical.noKnownMedications",
      "clinical.noKnownAllergies",
    ],
  },
  {
    id: "clinical-significance",
    title: "Clinical Significance / Diagnostic Testing Information",
    render: renderClinicalSignificance,
    requiredFieldPaths: [...headerPaths, "clinical.noKnownAllergies"],
  },
  {
    id: "dvt-assessment",
    title: "DVT Assessment",
    render: renderDvtAssessment,
    requiredFieldPaths: [
      ...headerPaths,
      "patient.heightInches",
      "patient.weightPounds",
      "case.surgeryDurationMinutes",
      "case.surgeryTypeCategory",
    ],
  },
  {
    id: "fall-risk",
    title: "Fall Risk Assessment",
    render: renderFallRisk,
    requiredFieldPaths: [...headerPaths],
  },
  {
    id: "medication-reconciliation",
    title: "Medication Reconciliation",
    render: renderMedicationReconciliation,
    requiredFieldPaths: [...headerPaths, "clinical.noKnownMedications"],
  },
  {
    id: "anesthesia-exam",
    title: "Anesthesia Physical Exam",
    render: renderAnesthesiaExam,
    requiredFieldPaths: [...headerPaths],
  },
  {
    id: "anesthesia-consent",
    title: "Consent for Anesthesia",
    render: renderAnesthesiaConsent,
    requiredFieldPaths: [...headerPaths],
  },
];

/**
 * The questionnaire Q&A listing is NOT part of the chart (Shannon,
 * 2026-09-01: "that will not be part of the chart, and should not be part
 * of the chart"). It survives as an internal worksheet outside the printed
 * pack so the no-orphan-answers coverage gate still has a printable home
 * for every answer and clinicians can pull the raw transcript of answers
 * when they need it.
 */
export const questionnaireWorksheetDocument: ChartPackDocumentDefinition = {
  id: "questionnaire-worksheet",
  title: "Questionnaire Worksheet (internal — not part of the chart)",
  render: renderHistoryForm,
  requiredFieldPaths: [...headerPaths],
};

export function renderQuestionnaireWorksheetHtml(
  record: ClinicianWorkbenchPatientRecordV1,
  questionnaireContent?: ChartPackQuestionnaireContent | null,
): string {
  return renderChartPackHtml({
    bodies: [
      `${docBanner(questionnaireWorksheetDocument.title, 1, 1)}${questionnaireWorksheetDocument.render(record, questionnaireContent)}`,
    ],
    title: questionnaireWorksheetDocument.title,
  });
}

export function computeChartPackDocumentGaps(
  record: ClinicianWorkbenchPatientRecordV1 | null,
  definition: ChartPackDocumentDefinition,
): ClinicianWorkbenchPatientRecordFieldPath[] {
  if (record == null) {
    return [...definition.requiredFieldPaths];
  }

  return definition.requiredFieldPaths.filter(
    (fieldPath) => !isPatientRecordFieldFilled(record, fieldPath),
  );
}

function renderDocumentBody(
  record: ClinicianWorkbenchPatientRecordV1,
  definition: ChartPackDocumentDefinition,
  questionnaireContent?: ChartPackQuestionnaireContent | null,
): string {
  const documentIndex =
    chartPackDocuments.findIndex(
      (candidate) => candidate.id === definition.id,
    ) + 1;

  return `${docBanner(definition.title, documentIndex, chartPackDocuments.length)}${definition.render(record, questionnaireContent)}`;
}

export function renderChartPackDocumentHtml(
  record: ClinicianWorkbenchPatientRecordV1,
  definition: ChartPackDocumentDefinition,
  questionnaireContent?: ChartPackQuestionnaireContent | null,
): string {
  return renderChartPackHtml({
    bodies: [renderDocumentBody(record, definition, questionnaireContent)],
    title: definition.title,
  });
}

export function renderFullChartPackHtml(
  record: ClinicianWorkbenchPatientRecordV1,
  questionnaireContent?: ChartPackQuestionnaireContent | null,
): string {
  return renderChartPackHtml({
    bodies: chartPackDocuments.map((definition) =>
      renderDocumentBody(record, definition, questionnaireContent),
    ),
    title: "Chart Pack",
  });
}
